/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './auth';
import { getOnboardingStatus } from './onboarding';

/**
 * OnboardingStatus context — single source of truth for professional onboarding progress.
 *
 * Design:
 *  - One HTTP call at mount, shared across all consumers (pill, banner, page, gating hooks).
 *  - Auto-fetch triggered when user becomes available. Skipped for patients and unauthenticated users.
 *  - `refresh()` allows children to force a re-fetch after mutating (e.g., saving a tier).
 *  - In-flight de-duplication via a ref-tracked promise, so parallel `refresh()` calls collapse to one HTTP.
 *  - Optional route-change re-fetch: enabled only after the user has completed at least one save,
 *    to keep the pill/banner up-to-date without hammering the API on every navigation.
 */

const OnboardingStatusContext = createContext({
  status: null,
  loading: false,
  error: null,
  refresh: async () => {},
});

export const OnboardingStatusProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // In-flight de-duplication
  const inflightRef = useRef(null);
  // Track whether we have ever fetched successfully (used to gate route-change refetch)
  const hasFetchedRef = useRef(false);

  const isPatient = !!user && (user.role === 'patient' || user.category === 'paziente');
  const canFetch = !!user && !isPatient;

  const fetchStatus = useCallback(async () => {
    if (!canFetch) {
      // Ensure any stale state is cleared when signing out or switching to a patient account
      setStatus(null);
      setLoading(false);
      return null;
    }
    if (inflightRef.current) return inflightRef.current;

    setLoading(true);
    setError(null);
    const p = getOnboardingStatus()
      .then((s) => {
        setStatus(s);
        hasFetchedRef.current = true;
        return s;
      })
      .catch((e) => {
        setError(e);
        // Only clear status on hard 401/403 (user no longer eligible)
        const code = e?.response?.status;
        if (code === 401 || code === 403) setStatus(null);
        return null;
      })
      .finally(() => {
        setLoading(false);
        inflightRef.current = null;
      });

    inflightRef.current = p;
    return p;
  }, [canFetch]);

  // Initial fetch when user becomes available / eligibility changes
  useEffect(() => {
    if (canFetch) {
      fetchStatus();
    } else {
      // Reset when logging out or switching account
      setStatus(null);
      setError(null);
      hasFetchedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, canFetch]);

  // Lightweight refetch on route change AFTER first successful load, so pill/banner reflect
  // recent mutations (e.g., user just finished Tier 1 on /onboarding then navigates back).
  useEffect(() => {
    if (!canFetch) return;
    if (!hasFetchedRef.current) return;
    // Skip refetch on the /onboarding page itself — the page manages its own state and
    // calls refresh() explicitly on every mutation.
    if (location.pathname.startsWith('/onboarding')) return;
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const refresh = useCallback(() => fetchStatus(), [fetchStatus]);

  return (
    <OnboardingStatusContext.Provider value={{ status, loading, error, refresh }}>
      {children}
    </OnboardingStatusContext.Provider>
  );
};

export const useOnboardingStatus = () => useContext(OnboardingStatusContext);
